import { lazy, Suspense, Component, type ReactNode, type ErrorInfo } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider, useAuth } from './context/AuthContext'
import { ThemeProvider } from './context/ThemeContext'
import { FloatingChatProvider } from './context/FloatingChatContext'
import { PersonaPopupProvider } from './components/PersonaPopup'
import { ToastProvider } from './components/Toast'
import { useSSEConnection, useSSEQueryInvalidation } from './hooks/useSSE'
import Layout from './components/Layout'
import LoginPage from './pages/LoginPage'

// Internal pages
import LobbyPage from './pages/internal/LobbyPage'
import ProjectsPage from './pages/internal/ProjectsPage'
import ProjectDetailPage from './pages/internal/ProjectDetailPage'
import TaskDetailPage from './pages/internal/TaskDetailPage'
import ConversationViewPage from './pages/internal/ConversationViewPage'
import ActivityPage from './pages/internal/ActivityPage'
import PersonasPage from './pages/internal/PersonasPage'
import SettingsPage from './pages/internal/SettingsPage'
import InboxPage from './pages/internal/InboxPage'
import CommunitiesPage from './pages/internal/CommunitiesPage'
// Bridge retired — Mira-Eli conversation migrated to North Star Studios community
import CeremonyLobbyPage from './pages/internal/CeremonyLobbyPage'
import JournalViewerPage from './pages/internal/JournalViewerPage'
import CeremonyRoomPage from './pages/internal/CeremonyRoomPage'
import OperationsPage from './pages/internal/OperationsPage'
import StatusDashboardPage from './pages/internal/StatusDashboardPage'
import EvolvePage from './pages/internal/EvolvePage'
import QuestCreatePage from './pages/internal/QuestCreatePage'
import QuestDetailPage from './pages/internal/QuestDetailPage'
import CurriculumDetailPage from './pages/internal/CurriculumDetailPage'
import PersonaEvolvePage from './pages/internal/PersonaEvolvePage'
import NewspaperPage from './pages/internal/NewspaperPage'
import TechPage from './pages/internal/TechPage'
import VideoReviewPage from './pages/internal/VideoReviewPage'
import OpenClawPage from './pages/internal/OpenClawPage'
import StudioPage from './pages/internal/studio/StudioPage'
import CanvasPage from './pages/internal/studio/CanvasPage'
import AwarenessPage from './pages/internal/AwarenessPage'
import LabPage from './pages/internal/LabPage'
import CodingLabPage from './pages/internal/CodingLabPage'
import KanaryPage from './pages/internal/KanaryPage'
import VideoCallPage from './pages/internal/VideoCallPage'
import CommunityUserDetailPage from './pages/internal/CommunityUserDetailPage'
import PersonaHomePage from './pages/internal/PersonaHomePage'
import UserPublicPage from './pages/internal/UserPublicPage'
import WikiIndexPage from './pages/internal/WikiIndexPage'
import WikiArticlePage from './pages/internal/WikiArticlePage'
import AgoraPage from './pages/internal/AgoraPage'
import SpaceDetailPage from './pages/internal/SpaceDetailPage'
import SeriesPage from './pages/internal/SeriesPage'
import FeedPage from './pages/internal/FeedPage'
import FeedSettingsPage from './pages/internal/FeedSettingsPage'

// Community pages
import CommunityLobbyPage from './pages/community/CommunityLobbyPage'
import CommunitySettingsPage from './pages/community/CommunitySettingsPage'
import CommunityBioPage from './pages/community/CommunityBioPage'
import CommunityDirectoryPage from './pages/community/CommunityDirectoryPage'
import CommunityConversationsPage from './pages/community/CommunityConversationsPage'
import CommunityFilesPage from './pages/community/CommunityFilesPage'
import CommunityMessagesPage from './pages/community/CommunityMessagesPage'
import CommunityProjectsPage from './pages/community/CommunityProjectsPage'

// Lazy-loaded (heavy deps — xterm.js)
const CompanionTerminalsPage = lazy(() => import('./pages/internal/CompanionTerminalsPage'))
const TerminalPage = lazy(() => import('./pages/internal/TerminalPage'))

// ─── Global Error Boundary ─────────────────────────────────────
class AppErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null; errorInfo: ErrorInfo | null }> {
  state: { error: Error | null; errorInfo: ErrorInfo | null } = { error: null, errorInfo: null }
  static getDerivedStateFromError(error: Error) { return { error } }
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo })
    console.error('[AppErrorBoundary]', error, errorInfo)
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 40, fontFamily: 'monospace', background: '#1a1a24', color: '#e6edf3', minHeight: '100vh' }}>
          <h1 style={{ color: '#f85149', fontSize: 18 }}>Something went wrong</h1>
          <pre style={{ color: '#ffa657', whiteSpace: 'pre-wrap', marginTop: 16, fontSize: 13 }}>
            {this.state.error.message}
          </pre>
          <pre style={{ color: '#8b949e', whiteSpace: 'pre-wrap', marginTop: 12, fontSize: 11, maxHeight: 400, overflow: 'auto' }}>
            {this.state.error.stack}
          </pre>
          <button
            onClick={() => { this.setState({ error: null, errorInfo: null }); window.location.href = '/conversations' }}
            style={{ marginTop: 20, padding: '8px 16px', background: '#238636', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}
          >
            Go to Talk
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 5_000, retry: 3, retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10_000), refetchOnWindowFocus: true } },
})

function SSEBridge() {
  useSSEConnection()
  useSSEQueryInvalidation()
  return null
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  if (isLoading) return null
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <><SSEBridge />{children}</>
}

function Loading() {
  return <div className="flex items-center justify-center h-64"><div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Loading...</div></div>
}

function AppRoutes() {
  const { isAuthenticated, isLoading, user } = useAuth()
  if (isLoading) return <Loading />
  const isCommunity = user?.userType === 'community'

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />} />

      <Route element={<RequireAuth><Layout /></RequireAuth>}>
        {/* Home — branches by user type */}
        <Route path="/" element={isCommunity ? <CommunityLobbyPage /> : <LobbyPage />} />

        {/* Core pages */}
        <Route path="/messages" element={isCommunity ? <CommunityMessagesPage /> : <InboxPage />} />
        <Route path="/projects" element={isCommunity ? <CommunityProjectsPage /> : <ProjectsPage />} />
        <Route path="/project/:id" element={<ProjectDetailPage />} />
        <Route path="/project/:id/task/:taskId" element={<TaskDetailPage />} />
        <Route path="/conversations" element={<CommunityConversationsPage />} />
        <Route path="/conversation/:id" element={<ConversationViewPage />} />
        <Route path="/activity" element={<ActivityPage />} />
        <Route path="/personas" element={<PersonasPage />} />
        <Route path="/neighborhood" element={<PersonasPage />} />
        <Route path="/neighborhood/:name" element={<PersonaHomePage />} />
        <Route path="/neighborhood/user/:communitySlug/:userId" element={<UserPublicPage />} />
        <Route path="/directory" element={isCommunity ? <CommunityDirectoryPage /> : <PersonasPage />} />
        <Route path="/bio" element={<CommunityBioPage />} />
        <Route path="/portal/:slug/bio" element={<CommunityBioPage />} />
        <Route path="/clients" element={<CommunitiesPage />} />
        <Route path="/evolve" element={<EvolvePage />} />
        <Route path="/evolve/quests/new" element={<QuestCreatePage />} />
        <Route path="/evolve/quests/:questId" element={<QuestDetailPage />} />
        <Route path="/evolve/curriculum/:curriculumId" element={<CurriculumDetailPage />} />
        <Route path="/evolve/persona/:name" element={<PersonaEvolvePage />} />
        <Route path="/ceremony" element={<CeremonyLobbyPage />} />
        <Route path="/ceremony/:ceremonyId" element={<CeremonyRoomPage />} />
        <Route path="/portal/:communityId/user/:userId" element={<CommunityUserDetailPage />} />
        <Route path="/files" element={<CommunityFilesPage />} />
        {/* Bridge retired — conversation migrated to North Star Studios community */}
        <Route path="/tech" element={<TechPage />} />
        <Route path="/tech/video-review" element={<VideoReviewPage />} />
        <Route path="/tech/openclaw" element={<OpenClawPage />} />
        <Route path="/tech/studio" element={<CanvasPage />} />
        <Route path="/tech/studio/:id" element={<CanvasPage />} />
        <Route path="/tech/studio/:id/editor" element={<StudioPage />} />
        <Route path="/tech/canvas/:id" element={<CanvasPage />} />
        <Route path="/tech/lab" element={<LabPage />} />
        <Route path="/tech/coding-lab" element={<CodingLabPage />} />
        <Route path="/tech/kanary" element={<KanaryPage />} />
        <Route path="/video/:room" element={<VideoCallPage />} />
        <Route path="/video" element={<VideoCallPage />} />
        <Route path="/operations" element={<OperationsPage />} />
        <Route path="/status" element={<StatusDashboardPage />} />
        <Route path="/paper" element={<NewspaperPage />} />
        <Route path="/awareness" element={<AwarenessPage />} />
        <Route path="/agora" element={<AgoraPage />} />
        <Route path="/agora/space/:slug" element={<SpaceDetailPage />} />
        <Route path="/agora/series/:name" element={<SeriesPage />} />
        <Route path="/feed" element={<FeedPage />} />
        <Route path="/feed/settings" element={<FeedSettingsPage />} />
        <Route path="/wiki" element={<WikiIndexPage />} />
        <Route path="/wiki/:slug" element={<WikiArticlePage />} />
        <Route path="/journals/:persona" element={<JournalViewerPage />} />
        <Route path="/journals" element={<JournalViewerPage />} />
        <Route path="/settings" element={isCommunity ? <CommunitySettingsPage /> : <SettingsPage />} />

        {/* Terminals (lazy) */}
        <Route path="/conversation/:id/terminals" element={<Suspense fallback={<Loading />}><CompanionTerminalsPage /></Suspense>} />
        <Route path="/terminal/:persona" element={<Suspense fallback={<Loading />}><TerminalPage /></Suspense>} />

        {/* Catch-all */}
        <Route path="*" element={
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <h1 className="text-lg font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>404</h1>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Page not found</p>
            </div>
          </div>
        } />
      </Route>
    </Routes>
  )
}

export default function App() {
  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <ThemeProvider>
              <FloatingChatProvider>
                <PersonaPopupProvider>
                  <ToastProvider>
                    <AppRoutes />
                  </ToastProvider>
                </PersonaPopupProvider>
              </FloatingChatProvider>
            </ThemeProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </AppErrorBoundary>
  )
}
